import { usePageCrumb } from '@ps/ui-kit/components/app-shell';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useBlurValidation } from '@ps/ui-kit/lib/use-blur-validation';
import { minChars } from '@ps/ui-kit/lib/validate';
import { ApiError } from '@ps/api-client/http';
import { consoleApi, instructionApi, type AlertLinkView, type InternalInstructionView } from '@ps/api-client/console';
import { Alert } from '@ps/ui-kit/components/alert';
import { Button } from '@ps/ui-kit/components/button';
import { Card, CardTitle, Details } from '@ps/ui-kit/components/card';
import { EmailHistory } from '../email-history';
import { Field } from '@ps/ui-kit/components/field';
import { Input } from '@ps/ui-kit/components/input';
import { Pill, type Tone } from '@ps/ui-kit/components/pill';
import { Empty, ErrorState, Loading } from '@ps/ui-kit/components/states';
import { amount, date, dateTime, money, tableAmount } from '@ps/ui-kit/lib/format';
import { Table } from '@ps/ui-kit/components/table';
import { ControlsTable, VERDICT_TONE } from './controls';
import { AlertLinks, BeneficiarySnapshot, beneficiaryTitle, MonitoringCheck, PaymentDocuments, ScreeningCalls } from './evidence';

// K-06 Instruction detail (console view): the frozen originator and beneficiary snapshots, the gate evaluation with
// every control, the screening calls (vendor ref, list version, latency, hits), rail submissions and responses, the
// ledger postings, the state timeline with reason codes, and the retain-until date. Opening it is audited
// (INSTRUCTION_VIEW): the originator snapshot is personal data.

const STATE_TONE: Record<string, Tone> = { SETTLED: 'ok', SUBMITTED: 'info', SUBMITTING: 'info', IN_REVIEW: 'warn', UNKNOWN: 'warn', FAILED: 'bad', REVERSED: 'neu', RETURNED: 'neu' };
const LABEL: Record<string, string> = { name: 'Name', accountRef: 'Account reference', customerIdNumber: 'Customer number', address: 'Address', idDocNumber: 'ID document number', dob: 'Date of birth', placeOfBirth: 'Place of birth' };
/** A rate × 1e8 as a plain decimal ("17120000" → "0.1712"), never through a float. */
function rate(e8: unknown): string {
  if (typeof e8 !== 'string' || !/^\d+$/.test(e8)) return '—';
  const frac = (BigInt(e8) % 100_000_000n).toString().padStart(8, '0').replace(/0+$/, '');
  return `${BigInt(e8) / 100_000_000n}${frac ? `.${frac}` : ''}`;
}
const Json = ({ v }: { v: unknown }) => <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded bg-canvas p-2 font-mono text-[11px] text-ink-2">{JSON.stringify(v, null, 1)}</pre>;

export function InstructionDetail({ id, app = 'console', canReconcile = false, onOpenAlert }: {
  id: string; app?: 'admin' | 'console'; canReconcile?: boolean;
  /** Console: open an alert's workspace, where a held payment is released or rejected by closing the alert. */
  onOpenAlert?: (a: AlertLinkView) => void;
}) {
  const q = useQuery({ queryKey: ['instruction', app, id], queryFn: () => instructionApi(app).get(id) });
  usePageCrumb(q.data ? String(q.data.instruction.urn ?? '') || null : null);
  if (q.isPending) return <Loading label="Loading the instruction…" />;
  if (q.error) {
    if (q.error instanceof ApiError && (q.error.status === 404 || q.error.status === 400)) return <Empty title="Instruction not found" />;
    return <ErrorState message="We couldn't load this instruction." onRetry={() => void q.refetch()} />;
  }
  return <View d={q.data} app={app} canReconcile={canReconcile} onOpenAlert={onOpenAlert} />;
}

function View({ d, app, canReconcile, onOpenAlert }: { d: InternalInstructionView; app: 'admin' | 'console'; canReconcile: boolean; onOpenAlert?: (a: AlertLinkView) => void }) {
  const i = d.instruction as Record<string, string | null> & { beneficiarySnapshot: Record<string, string> };
  const e = d.evaluation;
  // To another PayStreet customer (ADR-083): settled between the two balances, never sent to a rail.
  const internal = i.payoutMethod === 'INTERNAL';
  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-2">
        <h1 className="mr-2 font-mono text-xl font-bold text-ink">{i.urn}</h1>
        <Pill tone={STATE_TONE[i.state!] ?? 'neu'} upper>{i.state}</Pill>
        <span className="text-sm text-ink-3">Customer sees: “{d.customerStatus}”</span>
      </header>
      {i.state === 'UNKNOWN' && <Reconcile urn={i.urn!} app={app} allowed={canReconcile} />}
      {app === 'console' && d.demoRail && !internal && ['SUBMITTED', 'UNKNOWN', 'SETTLED'].includes(i.state!) && <DemoRail urn={i.urn!} state={i.state!} />}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardTitle>Payment</CardTitle>
          <Details rows={[
            ['Amount', amount(i.srcAmountMinor, i.srcCcy!)], ['Fee', amount(i.feeMinor, i.feeCcy!)],
            ['Beneficiary receives', amount(i.dstAmountMinor, i.dstCcy!)], ['Dealing rate', i.fxRateE8 ? rate(i.fxRateE8) : '—'], ['Reference rate', i.referenceRateE8 ? rate(i.referenceRateE8) : '—'],
            ['HKD equivalent', money(e.hkdEquivMinor)], ['Purpose', `${i.purposeCode}${i.purposeText ? ` · ${i.purposeText}` : ''}`], ['Source of funds', i.sourceOfFunds],
            ['Charges', internal ? '—' : i.chargeBearer], ['Payout', internal ? 'PayStreet transfer (internal)' : i.payoutMethod], ['Customer reference', i.customerReference],
            ['Created by', i.createdByName], ['Approved by', i.approvedByName], ['Instructed', dateTime(i.instructedAt)], ['Retain until', date(i.retainUntil)],
          ]} />
        </Card>
        <div className="space-y-4">
          <Card>
            <CardTitle>Originator (frozen, as sent)</CardTitle>
            <Details rows={Object.entries(d.originatorSnapshot).map(([k, v]) => [LABEL[k] ?? k, v])} />
            <p className="mt-3 break-all font-mono text-[11px] text-ink-3">Fingerprint {i.originatorFingerprint}</p>
          </Card>
          <Card>
            <CardTitle>{beneficiaryTitle(i.beneficiarySnapshot)}</CardTitle>
            <BeneficiarySnapshot snapshot={i.beneficiarySnapshot} />
          </Card>
        </div>
      </div>

      <Card>
        <CardTitle aside={<span className="text-xs text-ink-3">{dateTime(e.evaluatedAt)} · {e.latencyMs} ms · policy {e.policyVersion} · list {e.listVersion ?? '—'}</span>}>
          Gate evaluation <Pill tone={VERDICT_TONE[e.verdict]} upper className="ml-2">{e.verdict}</Pill>
        </CardTitle>
        <ControlsTable checks={e.checks} label="Gate controls" />
      </Card>

      <Card>
        <CardTitle>Screening calls (SCR-03)</CardTitle>
        <ScreeningCalls calls={d.screeningCalls} />
      </Card>

      <Card>
        <CardTitle>Transaction monitoring (Shufti)</CardTitle>
        <MonitoringCheck check={d.monitoring} />
      </Card>

      <Card>
        <CardTitle>Supporting documents</CardTitle>
        <PaymentDocuments files={d.documents} app={app} />
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardTitle>Timeline</CardTitle>
          <ol className="space-y-2 text-sm" aria-label="State history">
            {d.transitions.map(t => (
              <li key={t.seq} className="flex flex-wrap gap-x-3">
                <span className="font-mono text-xs text-ink-3">{dateTime(t.at)}</span>
                <span className="font-semibold text-ink">{t.fromState ?? 'NEW'} → {t.toState}</span>
                {t.reasonCode && <span className="font-mono text-xs text-warn">{t.reasonCode}</span>}
                <span className="text-ink-3">by {t.actor}</span>
              </li>
            ))}
          </ol>
          {d.alerts.length > 0 && (
            <div className="mt-4 border-t border-line pt-3">
              <p className="eyebrow">Alerts</p>
              {i.state === 'IN_REVIEW' && onOpenAlert && <p className="mt-1 text-xs text-ink-2">This payment is held. Release or reject it by closing its alert.</p>}
              <AlertLinks alerts={d.alerts} onOpen={onOpenAlert} />
            </div>
          )}
        </Card>
        <Card>
          <CardTitle>Ledger postings</CardTitle>
          {d.journals.map(j => (
            <div key={j.id} className="mb-3">
              <p className="text-sm"><span className="font-semibold">{j.type}</span> <span className="text-ink-3">· {j.memo} · {dateTime(j.at)}</span></p>
              <Table bare compact className="mt-1" label={`${j.type} postings`}>
                <tbody>
                  {j.postings.map((p, k) => (
                    <tr key={k}><td className="font-mono">{p.accountCode}</td><td className={`py-1 text-right tabular ${p.amountMinor.startsWith('-') ? 'text-bad' : 'text-ok'}`}>{tableAmount(p.amountMinor, p.currency)}</td></tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ))}
        </Card>
      </div>

      <Card>
        <CardTitle>Rail submissions</CardTitle>
        {internal ? <p className="text-sm text-ink-3">An internal transfer: settled between the two PayStreet balances and never sent to a rail.</p>
          : d.railOperations.length === 0 ? <p className="text-sm text-ink-3">Not submitted to the rail (held or refused).</p> : d.railOperations.map(o => (
          <div key={o.attemptNo} className="mb-4">
            <p className="text-sm"><span className="font-semibold">Attempt {o.attemptNo}</span> · {o.rail} · <Pill tone={o.status === 'SUBMITTED' || o.status === 'SUCCEEDED' ? 'ok' : o.status === 'FAILED' ? 'bad' : 'warn'} upper>{o.status}</Pill>
              {' '}· rail ref <span className="font-mono">{o.railRef ?? '—'}</span> · originator echo {o.originatorEchoOk === null ? '—' : o.originatorEchoOk ? 'intact' : 'CHANGED (WTR-08)'} · {dateTime(o.requestedAt)}</p>
            {o.error && <p className="text-sm text-bad">{o.error}</p>}
            <div className="mt-2 grid gap-2 lg:grid-cols-2">
              <details><summary className="cursor-pointer text-xs font-semibold text-ink-2">Request sent</summary><Json v={o.request} /></details>
              <details><summary className="cursor-pointer text-xs font-semibold text-ink-2">Response</summary><Json v={o.response} /></details>
            </div>
          </div>
        ))}
      </Card>
      {app === 'console' && <EmailHistory queryKey={['instruction', app, i.id]} load={() => instructionApi(app).emails(i.id!)} />}
    </div>
  );
}

/** G-21: the rail never confirmed this payment. Operations or the CO record what reconciliation showed. */
function Reconcile({ urn, app, allowed }: { urn: string; app: 'admin' | 'console'; allowed: boolean }) {
  const qc = useQueryClient();
  const [railRef, setRailRef] = React.useState('');
  const [evidence, setEvidence] = React.useState('');
  const v = useBlurValidation({ evidence: () => minChars(evidence, 5) });
  const m = useMutation({
    mutationFn: (outcome: 'ACCEPTED' | 'NOT_ACCEPTED') => instructionApi(app).reconcile(urn, { outcome, railRef: railRef.trim() || null, evidenceRef: evidence.trim() }),
    onSuccess: d => { qc.setQueryData(['instruction', app, urn], d); void qc.invalidateQueries({ queryKey: ['instruction'] }); void qc.invalidateQueries({ queryKey: ['reconcile'] }); void qc.invalidateQueries({ queryKey: ['work'] }); },
  });
  return (
    <Alert tone="warn" title="Outcome unknown: the payout partner never confirmed this payment">
      <p>The funds stay reserved. It is never sent again automatically. Check the partner's statement or ask the partner, then record the result.</p>
      {allowed ? (
        <div className="mt-3 grid gap-3 text-ink sm:grid-cols-2">
          <Field id="recon-evidence" label="Evidence (statement line, partner ticket)" error={v.errorOf('evidence')}><Input id="recon-evidence" value={evidence} maxLength={500} onChange={e => setEvidence(e.target.value)} {...v.field('evidence', 'recon-evidence')} /></Field>
          <Field id="recon-ref" label="Partner reference (if accepted)"><Input id="recon-ref" value={railRef} maxLength={80} onChange={e => setRailRef(e.target.value)} /></Field>
          <div className="flex flex-wrap gap-2 sm:col-span-2">
            <Button size="sm" disabled={m.isPending || evidence.trim().length < 5 || !railRef.trim()} onClick={() => m.mutate('ACCEPTED')}>The partner accepted it</Button>
            <Button size="sm" variant="destructive" disabled={m.isPending || evidence.trim().length < 5} onClick={() => m.mutate('NOT_ACCEPTED')}>Not accepted: release the funds</Button>
          </div>
          {m.error && <p role="alert" className="text-sm text-bad sm:col-span-2">{(m.error as Error).message}</p>}
        </div>
      ) : <p className="mt-2">Operations or the Compliance Officer resolve it.</p>}
    </Alert>
  );
}

/** Demo only (stub rail): make the partner send a status event through the real, signed webhook. */
function DemoRail({ urn, state }: { urn: string; state: string }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (type: 'ACCEPTED' | 'SETTLED' | 'FAILED' | 'RETURNED') => consoleApi.simulateRailEvent(urn, type),
    onSuccess: () => { window.setTimeout(() => void qc.invalidateQueries({ queryKey: ['instruction'] }), 1500); },
  });
  const types = state === 'SETTLED' ? (['RETURNED'] as const) : state === 'UNKNOWN' ? (['ACCEPTED', 'SETTLED', 'FAILED'] as const) : (['SETTLED', 'FAILED'] as const);
  return (
    <div className="rounded-lg border border-dashed border-line px-4 py-3 text-sm">
      <span className="font-semibold text-ink-2">Demo: simulate the payout partner</span>
      <span className="ml-2 inline-flex flex-wrap gap-2">
        {types.map(t => <Button key={t} size="sm" variant="ghost" disabled={m.isPending} onClick={() => m.mutate(t)}>{t.toLowerCase()}</Button>)}
      </span>
      {m.isSuccess && <span className="ml-2 text-ink-3">Event sent; the page refreshes when it is applied.</span>}
      {m.error && <span className="ml-2 text-bad">{(m.error as { status?: number }).status === 404 ? 'Not available in this environment.' : (m.error as Error).message}</span>}
    </div>
  );
}
